import { Type } from "class-transformer";
import { AbstractJSONModel } from "./master-model";
import { isNorwegianMarket } from "@/countries";

export enum eAgreement {
  // Danish agreements (OK11)
  YearNorm = 0,
  DayNorm = 1,
  Student = 2,
  HourlyPaid = 3,
  PrivateAgreement = 4,
  // Danish agreements (OK13)
  OK13 = 5,
  Student_OK13 = 6,
  DayNorm_OK13 = 7,
  HourlyPaid_OK13 = 8,
  OK13_SchoolHoursUsePool = 9,
  DayNorm_OK13_SchoolHoursUsePool = 10,
  // Danish agreements (OK21)
  OK21_1924Hours = 11,
  OK21_CalculatedHours = 12,
  Student_OK21 = 13,
  DayNorm_OK21 = 14,
  HourlyPaid_OK21 = 15,
  // Norwegian agreements
  NO_YearNorm = 16,
  NO_DayNorm = 17,
  fake = 100,
}

const DANISH_AGREEMENTS = [
  eAgreement.YearNorm,
  eAgreement.DayNorm,
  eAgreement.Student,
  eAgreement.HourlyPaid,
  eAgreement.PrivateAgreement,
  eAgreement.OK13,
  eAgreement.Student_OK13,
  eAgreement.DayNorm_OK13,
  eAgreement.HourlyPaid_OK13,
  eAgreement.OK13_SchoolHoursUsePool,
  eAgreement.DayNorm_OK13_SchoolHoursUsePool,
  eAgreement.OK21_1924Hours,
  eAgreement.OK21_CalculatedHours,
  eAgreement.Student_OK21,
  eAgreement.DayNorm_OK21,
  eAgreement.HourlyPaid_OK21,
];

const NORWEGIAN_AGREEMENTS = [eAgreement.NO_YearNorm, eAgreement.NO_DayNorm];

export function getAgreementsForMarket(): eAgreement[] {
  return isNorwegianMarket() ? NORWEGIAN_AGREEMENTS : DANISH_AGREEMENTS;
}

export interface IPositionCategory {
  id: number;
  name: string;
  norm_period_type_id: number;
  created_at: Date;
  updated_at: Date;
  institution_id: number | null;
  other_time_norm: number | null;
  agreement: eAgreement | null;
  fixed_other_time: boolean | null;
}

export interface IPositionCategoryPost {
  name: string;
  norm_period_type_id: number;
  institution_id: number | null;
  other_time_norm: number | null;
  agreement: eAgreement | null;
  fixed_other_time: boolean | null;
}

export class PositionCategory
  extends AbstractJSONModel
  implements IPositionCategory
{
  public id = -1;
  public name = "";
  public norm_period_type_id = -1;
  @Type(() => Date)
  public created_at: Date = new Date();
  @Type(() => Date)
  public updated_at: Date = new Date();
  public institution_id: number | null = null;
  public other_time_norm: number | null = null;
  public agreement: eAgreement | null = null;
  public fixed_other_time: boolean | null = null;

  isDkOK13() {
    return (
      this.agreement === eAgreement.DayNorm_OK13_SchoolHoursUsePool ||
      this.agreement === eAgreement.DayNorm_OK13 ||
      this.agreement === eAgreement.HourlyPaid_OK13 ||
      this.agreement === eAgreement.OK13 ||
      this.agreement === eAgreement.OK13_SchoolHoursUsePool ||
      this.agreement === eAgreement.Student_OK13
    );
  }

  isDkOK21() {
    return (
      this.agreement === eAgreement.OK21_1924Hours ||
      this.agreement === eAgreement.OK21_CalculatedHours ||
      this.agreement === eAgreement.Student_OK21 ||
      this.agreement === eAgreement.DayNorm_OK21 ||
      this.agreement === eAgreement.HourlyPaid_OK21
    );
  }

  isDkOK11() {
    return (
      this.agreement === eAgreement.DayNorm ||
      this.agreement === eAgreement.HourlyPaid ||
      this.agreement === eAgreement.Student ||
      this.agreement === eAgreement.PrivateAgreement ||
      this.agreement === eAgreement.YearNorm
    );
  }

  isDanishAgreement() {
    return this.isDkOK11() || this.isDkOK13() || this.isDkOK21();
  }

  isNorwegianAgreement() {
    return (
      this.agreement === eAgreement.NO_YearNorm ||
      this.agreement === eAgreement.NO_DayNorm
    );
  }

  supervisionExcludesVacation() {
    return this.isDkOK21() || this.isNorwegianAgreement();
  }

  vacationIncludesSixthWeek() {
    return this.isDkOK11() || this.isDkOK13();
  }

  canUseCorrection() {
    return this.isNorwegianAgreement() || this.isDkOK21();
  }

  isStudent() {
    return (
      this.agreement === eAgreement.Student ||
      this.agreement === eAgreement.Student_OK13 ||
      this.agreement === eAgreement.Student_OK21
    );
  }

  isHourlyPaid() {
    return (
      this.agreement === eAgreement.HourlyPaid ||
      this.agreement === eAgreement.HourlyPaid_OK13 ||
      this.agreement === eAgreement.HourlyPaid_OK21
    );
  }

  public convertToPostObject(): IPositionCategoryPost {
    const p: IPositionCategoryPost = Object.assign({});
    p.agreement = this.agreement;
    p.fixed_other_time = this.fixed_other_time;
    p.institution_id = this.institution_id;
    p.name = this.name;
    p.norm_period_type_id = this.norm_period_type_id;
    p.other_time_norm = this.other_time_norm;
    return p;
  }

  equals(other: IPositionCategory): boolean {
    return other.id === this.id;
  }
}
